import React, { useState, useRef } from 'react';

// Bulk broker-team CSV import (15 July 2026) — the other half of the
// original "import your list in 30 seconds" homepage spec; the introducer
// half (BulkInviteModal.jsx) was built 10 July 2026.
// Flow: pick/paste CSV -> POST /api/broker/invites/bulk-preview (validates,
// nothing created) -> review table with per-row errors/warnings -> confirm
// -> POST /api/broker/invites/bulk.
// isManager (non-owner) narrows what the template/copy suggests, but the
// real enforcement is server-side (a manager row is always rejected, and
// manager_email is always overridden to the requester) — this component
// just mirrors that back so the preview table isn't surprising.

const TEMPLATE_CSV =
  'name,email,role,manager_email,frn_number\n' +
  'Jane Adviser,jane@example.com,adviser,manager@yourcompany.com,123456\n' +
  'John Manager,john@example.com,manager,,\n';

const token = () => localStorage.getItem('token');

export default function BulkTeamInviteModal({ onClose, onSuccess, isManager = false }) {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'team-import-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(String(ev.target.result || ''));
    reader.readAsText(f);
  };

  const runPreview = async () => {
    if (!csvText.trim()) { setError('Choose a CSV file (or paste CSV text) first'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/broker/invites/bulk-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ csv: csvText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to read CSV');
      setPreview(data);
      setStep('preview');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const sendInvites = async () => {
    const rows = preview.rows.filter(r => r.ok).map(r => r.data);
    if (rows.length === 0) return;
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/broker/invites/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ rows }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setResult(data);
      setStep('done');
      if (onSuccess) onSuccess();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const s = preview?.summary;

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={st.title}>Bulk Import Team</h2>
          <button onClick={onClose} style={st.close}>✕</button>
        </div>

        {step === 'upload' && (
          <>
            <p style={st.sub}>
              Upload a CSV of your teammates. Each valid row sends the same "set your
              password" invite email as the "Invite teammate" button — nothing is
              created until you review and confirm.
              {isManager && ' As a manager, every row you import is added as an adviser on your own team.'}
            </p>
            <div style={st.uploadBox} onClick={() => fileRef.current?.click()}>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: 'none' }} />
              <div style={{ fontSize: 30 }}>📄</div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c' }}>
                {fileName || 'Click to choose a CSV file'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Columns: name, email, role (manager or adviser), manager_email, frn_number
              </div>
            </div>
            <button onClick={downloadTemplate} style={st.linkBtn}>⬇ Download CSV template</button>
            {error && <div style={st.error}>{error}</div>}
            <div style={st.footer}>
              <button onClick={onClose} style={st.secondary}>Cancel</button>
              <button onClick={runPreview} disabled={loading || !csvText.trim()}
                style={{ ...st.primary, opacity: loading || !csvText.trim() ? 0.5 : 1 }}>
                {loading ? 'Checking…' : 'Preview import'}
              </button>
            </div>
          </>
        )}

        {step === 'preview' && preview && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 14px' }}>
              <span style={st.chip('#eff6ff', '#1d4ed8')}>{s.total} row{s.total === 1 ? '' : 's'}</span>
              <span style={st.chip('#d1fae5', '#065f46')}>{s.valid} ready to send</span>
              {s.invalid > 0 && <span style={st.chip('#fee2e2', '#991b1b')}>{s.invalid} skipped (errors)</span>}
            </div>
            <div style={st.tableWrap}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['#', 'Name', 'Email', 'Role', 'FRN', 'Status'].map(h => (
                      <th key={h} style={st.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map(r => (
                    <tr key={r.row} style={{ background: r.ok ? '#fff' : '#fef2f2' }}>
                      <td style={st.td}>{r.row}</td>
                      <td style={st.td}>{r.data.name || '—'}</td>
                      <td style={st.td}>{r.data.email || '—'}</td>
                      <td style={st.td}>{r.data.role || '—'}</td>
                      <td style={st.td}>{r.data.frn_number || '—'}</td>
                      <td style={st.td}>
                        {r.ok
                          ? <span style={{ color: '#065f46', fontWeight: 600 }}>OK</span>
                          : <span style={{ color: '#991b1b' }}>{r.errors.join('; ')}</span>}
                        {r.ok && r.warnings.length > 0 && (
                          <span style={{ color: '#92400e', display: 'block' }}>⚠ {r.warnings.join('; ')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {error && <div style={st.error}>{error}</div>}
            <div style={st.footer}>
              <button onClick={() => { setStep('upload'); setPreview(null); setError(''); }} style={st.secondary}>← Back</button>
              <button onClick={sendInvites}
                disabled={loading || s.valid === 0}
                style={{ ...st.primary, opacity: loading || s.valid === 0 ? 0.5 : 1 }}>
                {loading ? 'Sending…' : `Send ${s.valid} invite${s.valid === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}

        {step === 'done' && result && (
          <>
            <div style={{ textAlign: 'center', padding: '18px 0 6px' }}>
              <div style={{ fontSize: 40 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0e1b2c', marginTop: 6 }}>
                {result.created} invite{result.created === 1 ? '' : 's'} sent
              </div>
              {result.failed > 0 && (
                <div style={{ fontSize: 13, color: '#991b1b', marginTop: 4 }}>{result.failed} row{result.failed === 1 ? '' : 's'} failed</div>
              )}
            </div>
            {result.failed > 0 && (
              <div style={{ ...st.tableWrap, maxHeight: 140 }}>
                {result.results.filter(r => !r.ok).map(r => (
                  <div key={r.row} style={{ fontSize: 12, color: '#991b1b', padding: '4px 0' }}>
                    Row {r.row} ({r.email || 'no email'}): {(r.errors || []).join('; ')}
                  </div>
                ))}
              </div>
            )}
            <div style={st.footer}>
              <button onClick={onClose} style={st.primary}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(14,27,44,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: '#fff', borderRadius: 14, padding: '22px 26px', width: '100%', maxWidth: 720, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: '#0e1b2c' },
  close: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#6b7280', padding: 4 },
  sub: { fontSize: 13, color: '#6b7280', margin: '8px 0 14px', lineHeight: 1.5 },
  uploadBox: { border: '2px dashed #d1d5db', borderRadius: 12, padding: '26px 16px', textAlign: 'center', cursor: 'pointer', background: '#f9fafb' },
  linkBtn: { background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: '10px 0 0', textDecoration: 'underline' },
  error: { background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 12 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 },
  primary: { background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  secondary: { background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 16px', fontWeight: 500, fontSize: 13, cursor: 'pointer' },
  tableWrap: { border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 300, overflowY: 'auto', marginTop: 4 },
  th: { position: 'sticky', top: 0, background: '#f9fafb', textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '7px 10px', borderBottom: '1px solid #f3f4f6', color: '#0e1b2c', verticalAlign: 'top' },
  chip: (bg, color) => ({ background: bg, color, fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999 }),
};
